import { useQuery } from "@tanstack/react-query"
import { SkeletonLines } from "components/new-stuff/ui/Skeleton"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import React, { useRef } from "react"
import { useAppNotificationStore, useAuthStore } from "stores"
import { walletAxiosInstance } from "utils/request"
import { ITransferResponse } from "./interface"
import { formatCurrencyNoSymbol } from "utils/currency"
import { Tag, Tooltip } from "antd"
import {
  ClipboardDocumentIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline"
import { formatDateReadable, formatTimeReadble } from "utils/formatDate"
import { availableFlags } from "utils/flags"
import useCopyToClipboard from "hooks/useCopyToClipboard"
import ReactToPrint from "react-to-print"
import { Button } from "components"
import { ICurrencies } from "pages/dashboard/wallets/integration/interface/kyc"
import { COMMON_ENDPOINTS, commonService } from "services/commonService"

const ViewTransferDetails = ({
  reference,
  onClose,
  open,
  showFee = true,
}: {
  reference: string
  onClose: () => void
  open: boolean
  showFee?: boolean
}) => {
  const componentRef: any = useRef()
  const { toast } = useAppNotificationStore()
  const [_, copy] = useCopyToClipboard()
  const { isLoading: isTransfersLoading, data: transferData } =
    useQuery<ITransferResponse>({
      queryKey: [`payment-transactions`, reference],
      queryFn: async () => {
        const res = await walletAxiosInstance.get(
          `/v1/wallets/payment-transactions/${reference}`
        )
        return res.data
      },

      // keepPreviousData: true,
      refetchOnWindowFocus: false,
      cacheTime: 24 * 60 * 60 * 1000,
      onSuccess: (data: any) => {},
    })
  const foundCurrency = availableFlags?.find(
    (curr) => curr.code === transferData?.data?.currency
  )
  const { isLoading: isCurrencyLoading, data: currencies } = useQuery<
    IBaseRes<ICurrencies[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_CURRENCIES],
    queryFn: () => commonService.getCurrencies(),
  })

  const paymentDetails = [
    {
      title: "Recipient Detail",
      value: transferData?.data?.accountName
        ? transferData?.data?.accountName
        : transferData?.data?.beneficiaryFirstName
        ? `${transferData?.data?.beneficiaryFirstName} ${transferData?.data?.beneficiaryLastName}`
        : "-",
    },
    {
      title: "Account Number",
      value: transferData?.data?.accountNumber ?? "-",
    },
    showFee
      ? {
          title: "Fee",
          value: transferData?.data?.fee
            ? `${foundCurrency?.symbol} ${formatCurrencyNoSymbol(
                Number(transferData?.data?.fee)
              )}`
            : "-",
        }
      : null,

    { title: "Transaction Type", value: transferData?.data?.type ?? "-" },
    { title: "Status", value: transferData?.data?.status ?? "-" },
    { title: "Narration", value: transferData?.data?.description ?? "-" },
    {
      title: "Ref",
      value: transferData?.data?.reference ?? "-",
      copy: transferData?.data?.reference,
    },
    transferData?.data?.sessionId
      ? {
          title: "Session ID",
          value: transferData?.data?.sessionId,
          copy: transferData?.data?.sessionId,
        }
      : null,
  ].filter(Boolean)

  const currencyInfo = currencies?.data?.find((flag) => {
    const item = transferData?.data?.meta?.destinationCurrency || ""

    return flag.code === item
  })
  return (
    <CustomModal
      title={<span className="opacity-0 ">dererß</span>}
      open={open}
      onCancel={onClose}
    >
      {isTransfersLoading ? (
        <div
          className="my-10"
          aria-busy="true"
        >
          <SkeletonLines lines={5} />
        </div>
      ) : (
        <div
          ref={componentRef}
          className=" flex flex-col items-center"
        >
          <div className="mb-5 flex flex-col items-center">
            <h2 className="font-circular text-[32px] font-bold mb-1">
              {foundCurrency?.symbol}
              {formatCurrencyNoSymbol(Number(transferData?.data?.amount))}
            </h2>
            {transferData?.data?.meta?.sourceCurrency && (
              <p>
                {currencyInfo?.symbol}
                {formatCurrencyNoSymbol(
                  Number(
                    transferData?.data?.meta?.destinationAmount ??
                      transferData?.data?.meta?.receivingAmount
                  )
                )}
              </p>
            )}
          </div>
          <div className="flex items-center mb-5">
            <Tag
              color={
                transferData?.data?.status === "successful"
                  ? "success"
                  : transferData?.data?.status === "failed"
                  ? "error"
                  : transferData?.data?.status === "processing"
                  ? "warning"
                  : "default"
              }
            >
              {transferData?.data?.status}
            </Tag>
            {transferData?.data?.status === "failed" && (
              <Tooltip
                title={transferData?.data?.statusMessage}
                className=" cursor-pointer"
              >
                <InformationCircleIcon className="h-4 w-4" />
              </Tooltip>
            )}
          </div>
          <p className="flex text-subText3 text-sm">
            {transferData?.data?.createdAt &&
              formatDateReadable(String(transferData?.data?.createdAt))}{" "}
            {transferData?.data?.createdAt &&
              formatTimeReadble(String(transferData?.data?.createdAt))}
          </p>
          <div className=" mt-[32px] bg-[rgba(204,219,253,0.4)] px-[22px] py-[26px] w-full rounded">
            <h4 className="text-base font-avenir text-padeBlack font-bold mb-[27px]">
              Transaction Details
            </h4>
            <div className=" flex flex-col gap-5">
              {paymentDetails?.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between gap-4"
                >
                  <p className="text-sm font-thin shrink-0">{item?.title}</p>
                  <p className="text-padeBlack font-circular text-sm font-medium flex items-center min-w-0">
                    <span
                      className="truncate print:whitespace-normal print:break-words"
                      title={String(item?.value)}
                    >
                      {item?.value}
                    </span>
                    {item?.copy && (
                      <button
                        onClick={() => {
                          copy(String(item?.copy))
                          toast.success("Copied")
                        }}
                        className="ml-0 sm:ml-2 shrink-0  text-primary500 p-1 rounded-full"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mt-2 flex justify-end">
        {transferData && componentRef && (
          <ReactToPrint
            trigger={() => (
              <Button
                color="primary"
                title="download"
              >
                Download
              </Button>
            )}
            content={() => componentRef.current}
            documentTitle={`${transferData?.data?.reference}.pdf`}
          />
        )}
      </div>
    </CustomModal>
  )
}

export default ViewTransferDetails
